import { ChevronLeft } from 'lucide-react';
import { useSectorInsightsList, useSectorInsightsSources } from './useSectorInsights';
import { SectorInsightsStocksTab } from './SectorInsightsStocksTab';
import { SectorInsightsSourcesTab } from './SectorInsightsSourcesTab';
import { formatMarketCapKrw } from './format';
import type { SectorInsightsTabId } from './types';

interface Props {
  canonicalKey: string;
  tab: SectorInsightsTabId;
  onBack: () => void;
  onTabChange: (tab: SectorInsightsTabId) => void;
}

export function SectorInsightsDetail({
  canonicalKey,
  tab,
  onBack,
  onTabChange,
}: Props): JSX.Element {
  // 리스트 응답에서 메타(원천 섹터 수/종목 수/시가총액)를 추출 — 이미 캐시됨.
  const { data: listData } = useSectorInsightsList();
  const meta = listData?.items.find((it) => it.canonical_key === canonicalKey) ?? null;

  // trading_date 는 list / sources / stocks 응답 어디에나 동일하게 들어있다 (백엔드 가정).
  // sources 응답은 페이지네이션이 없어 가볍고, sources 탭 hook 과 dedupe 되므로 추가 비용 없음.
  // list 응답에 trading_date 가 있으면 그것을 우선 사용.
  const { data: sourcesData } = useSectorInsightsSources(canonicalKey);
  const tradingDate = listData?.trading_date ?? sourcesData?.trading_date ?? null;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <button type="button" className="pca-btn" onClick={onBack}>
          <ChevronLeft size={14} strokeWidth={1.75} />
          목록으로
        </button>
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>
          {meta?.canonical_display_name ?? canonicalKey}
        </h2>
        <code style={{ opacity: 0.6 }}>{canonicalKey}</code>
        <span
          className="pca-muted"
          style={{ fontSize: '0.8rem', marginLeft: 'auto' }}
          data-testid="sector-insights-detail-trading-date"
        >
          기준일: {tradingDate ? <code>{tradingDate}</code> : '—'}
        </span>
      </div>

      <div
        className="pca-table-wrap"
        style={{ padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 24, flexWrap: 'wrap' }}
      >
        <Metric label="원천 섹터 수" value={meta ? meta.source_sector_count.toLocaleString('ko-KR') : '—'} />
        <Metric label="총 종목 수" value={meta ? meta.stock_count.toLocaleString('ko-KR') : '—'} />
        <Metric label="시가총액 (합계)" value={meta ? formatMarketCapKrw(meta.total_market_cap) : '—'} />
      </div>

      <nav className="tx-tabs" role="tablist" aria-label="섹터 인사이트 상세 탭">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'stocks'}
          className={`tx-tab${tab === 'stocks' ? ' tx-tab--active' : ''}`}
          onClick={() => onTabChange('stocks')}
        >
          종목
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'sources'}
          className={`tx-tab${tab === 'sources' ? ' tx-tab--active' : ''}`}
          onClick={() => onTabChange('sources')}
        >
          원천 섹터
        </button>
      </nav>

      <section>
        {tab === 'stocks' && <SectorInsightsStocksTab canonicalKey={canonicalKey} />}
        {tab === 'sources' && <SectorInsightsSourcesTab canonicalKey={canonicalKey} />}
      </section>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: '0.75rem', opacity: 0.65 }}>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{value}</span>
    </div>
  );
}
